import axios from "axios";
import { DEFAULT_EXTENSION, MAX_IMAGE_SIZE_MB } from "../const/inventario";

export const normalize = (s) =>
  (s || "")
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const buildUrl = (baseUrl, pathParams = {}, queryParams = {}) => {
  let url = baseUrl;

  // Reemplazar path params
  Object.entries(pathParams).forEach(([key, value]) => {
    url = url.replace(`:${key}`, encodeURIComponent(value));
  });

  // Agregar query params
  const query = new URLSearchParams(queryParams).toString();
  return query ? `${url}?${query}` : url;
};

export const encodeSectionsBaseUrl = (url, param) => {
  if (!url || typeof url !== "string") {
    throw new Error("La URL base es obligatoria y debe ser una cadena válida.");
  }

  if (param == null || param === "") {
    return url;
  }
  return `${url}/${encodeURIComponent(param)}`;
};

/**
 * Reemplaza placeholders dinámicos en una URL template
 * @param {string} urlTemplate - Template con placeholders estilo :param o {{param}}
 * @param {Object} params - Objeto con valores a reemplazar
 * @returns {string} URL con parámetros codificados
 * @throws {Error} Si faltan parámetros requeridos
 */
export const encodeMultipleSectionsBaseUrl = (urlTemplate, params = {}) => {
  if (!urlTemplate || typeof urlTemplate !== "string") {
    throw new Error("El template URL es obligatorio y debe ser string.");
  }

  let url = urlTemplate;
  const missingParams = [];

  // Reemplazar placeholders {{param}} - NO codificar si contiene /
  url = url.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (params[key] == null) {
      missingParams.push(key);
      return match;
    }
    // Si el valor contiene '/', asumir que es parte de la ruta base
    const value = String(params[key]);
    return value.includes("/") ? value : encodeURIComponent(value);
  });

  // Reemplazar placeholders :param - SIEMPRE codificar (son valores dinámicos)
  url = url.replace(/:(\w+)/g, (match, key) => {
    if (params[key] == null) {
      missingParams.push(key);
      return match;
    }
    return encodeURIComponent(params[key]);
  });

  if (missingParams.length > 0) {
    throw new Error(`Parámetros requeridos faltantes: ${missingParams.join(", ")}`);
  }

  return url;
};

export const ok = (res) => {
  const response = res?.data ?? res;
  return response;
};

/**
 * Maneja de errores de solicitudes HTTP. *
 * @param {any} err - Error capturado en el bloque try/catch.
 * @param {Object} options - Opciones para enriquecer el log.
 * @param {string} [options.message] - Mensaje descriptivo del contexto del error.
 * @param {Object} [options.context] - Información adicional (payload, params, etc.) para depuración.
 * @throws {any} Relanza el error normalizado.
 */
export const errorHandler = (err, { message, context } = {}) => {
  console.error(message || "🟥 Error en la solicitud HTTP", {
    error: err?.response || err,
    ...(context && { context }),
  });

  throw err?.response?.data || err;
};

export const handlerError = (err, errorMessage) => {
  let normalizedMessage = errorMessage;

  if (axios.isAxiosError(err)) {
    // 🔹 Error de respuesta del servidor (4xx / 5xx)
    if (err.response) {
      const backendMsg = err.response.data?.message || err.response.data?.error;

      if (backendMsg) {
        normalizedMessage = backendMsg;
      } else if (err.response.status >= 500) {
        normalizedMessage = "El servicio no está disponible en este momento. Inténtalo más tarde.";
      } else {
        normalizedMessage = "No se pudo actualizar el estado del almacén.";
      }
    }
    // 🔹 Error de red / no hubo respuesta (aquí sale normalmente "Network Error")
    else if (err.request) {
      normalizedMessage = "No se pudo contactar al servidor. Inténtalo más tarde.";
    }
    // 🔹 Algo pasó al crear la petición
    else {
      normalizedMessage = "Ocurrió un error al preparar la solicitud. Inténtalo más tarde.";
    }
  } else if (err instanceof Error) {
    // Cualquier otro error no Axios
    normalizedMessage = err.message || errorMessage;
  }

  return normalizedMessage;
};

/**
 * Verifica que el archivo sea una imagen (mime type).
 */
export const isValidImageType = (file) => {
  if (!file?.type) return false;
  return file.type.startsWith("image/");
};

/**
 * Verifica que el archivo pese menos que maxMB.
 */
export const isValidImageSize = (file) => {
  if (!file || typeof file.size !== "number") return false;
  const sizeInMB = file.size / 1024 / 1024;
  return sizeInMB <= MAX_IMAGE_SIZE_MB;
};

/**
 * Extrae solo la parte base64 de un dataURL.
 * Ej: "data:image/jpeg;base64,AAAAA" -> "AAAAA"
 */
export const extractBase64FromDataUrl = (dataUrl) => {
  if (typeof dataUrl !== "string") return "";
  const parts = dataUrl.split(",");
  return parts.length === 2 ? parts[1] : dataUrl;
};

/**
 * Obtiene la extensión del archivo a partir de:
 *  1) file.type (mime) si existe
 *  2) file.name si no hay type
 */
export const getFileExtension = (file) => {
  if (!file) return DEFAULT_EXTENSION;

  // 1. Intentar desde MIME type
  if (file.type && file.type.includes("/")) {
    const [, ext] = file.type.split("/");
    if (ext) return ext.toLowerCase();
  }

  // 2. Intentar desde el nombre
  if (file.name && typeof file.name === "string") {
    const dotIndex = file.name.lastIndexOf(".");
    if (dotIndex !== -1 && dotIndex < file.name.length - 1) {
      return file.name.substring(dotIndex + 1).toLowerCase();
    }
  }

  return DEFAULT_EXTENSION;
};

/**
 * Verifica si ya existe un almacén con el mismo nombre (case-insensitive).
 * @param {Array} stores - Array de almacenes existentes
 * @param {string} name - Nombre del almacén a verificar
 * @param {number|null} excludeId - ID del almacén a excluir de la verificación (para updates)
 * @returns {boolean} true si el nombre está duplicado, false si es único
 */
export const isDuplicateStoreName = (stores, name, excludeId = null) => {
  return stores.some(
    (store) =>
      store.name.toLowerCase() === name.toLowerCase() && store.id !== excludeId && store.id !== 0 // Excluir "Todos"
  );
};
